// src/components/Frontend/BlogPosts.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { shouldShowApiToast } from '../../utils/helpers';
import {
  getBlogPosts,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost,
  toggleBlogPostStatus,
  exportBlogPosts,
  clearApiMessage,
  clearError,
  clearState,
  clearCurrentItem,
  selectBlogPosts,
  selectBlogPostPagination,
  selectCurrentBlogPost,
  selectBlogPostsLoading,
  selectBlogPostsSubmitting,
  selectBlogPostsApiMessage,
  selectBlogPostsError,
} from '../../store/slices/frontend/blogPostSlice';
import {
  Pencil,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Search,
  Loader2,
  Plus,
  ToggleLeft,
  ToggleRight,
  FileText,
  LayoutGrid,
  List,
  ClipboardList,
  Save,
  Clock,
  Eye,
  Star,
  Image,
} from 'lucide-react';

const BlogPosts = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  // Refs for add
  const titleRef = useRef(null);
  const excerptRef = useRef(null);
  const contentRef = useRef(null);
  const imageRef = useRef(null);
  const categoryRef = useRef(null);
  const authorRef = useRef(null);
  const authorRoleRef = useRef(null);
  const authorBioRef = useRef(null);
  const tagRef = useRef(null);
  const publishedRef = useRef(null);
  const readTimeRef = useRef(null);
  const isFeaturedRef = useRef(null);
  const isTrendingRef = useRef(null);
  const orderRef = useRef(null);
  const activeRef = useRef(null);

  // Refs for edit
  const editTitleRef = useRef(null);
  const editExcerptRef = useRef(null);
  const editContentRef = useRef(null);
  const editImageRef = useRef(null);
  const editCategoryRef = useRef(null);
  const editAuthorRef = useRef(null);
  const editAuthorRoleRef = useRef(null);
  const editAuthorBioRef = useRef(null);
  const editTagRef = useRef(null);
  const editPublishedRef = useRef(null);
  const editReadTimeRef = useRef(null);
  const editIsFeaturedRef = useRef(null);
  const editIsTrendingRef = useRef(null);
  const editOrderRef = useRef(null);
  const editActiveRef = useRef(null);

  const editingIdRef = useRef(null);
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  const items = useSelector(selectBlogPosts) || [];
  const pagination = useSelector(selectBlogPostPagination) || { current_page: 1, last_page: 1, per_page: 15, total: 0 };
  const loading = useSelector(selectBlogPostsLoading);
  const isSubmittingRedux = useSelector(selectBlogPostsSubmitting);
  const apiMessage = useSelector(selectBlogPostsApiMessage);
  const error = useSelector(selectBlogPostsError);

  useEffect(() => {
    dispatch(getBlogPosts({ per_page: itemsPerPage, page: currentPage }));
    return () => dispatch(clearState());
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const params = { search: searchTerm, per_page: itemsPerPage, page: currentPage };
      if (statusFilter) params.is_active = statusFilter === 'active' ? true : false;
      dispatch(getBlogPosts(params));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, currentPage, itemsPerPage, dispatch]);

  useEffect(() => {
    const msg = apiMessage || (error ? error : null);
    if (!msg || !showToast || !shouldShowApiToast(msg, !!error)) return;
    const messageKey = `${msg}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    if (lastToastRef.current.message === messageKey && (now - lastToastRef.current.timestamp) < 2000) return;
    lastToastRef.current = { message: messageKey, timestamp: now };
    showToast(msg, error ? 'error' : 'success');
    setTimeout(() => { dispatch(clearApiMessage()); dispatch(clearError()); }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  const showToastMessage = useCallback((msg, type = 'info') => {
    if (showToast && typeof showToast === 'function') showToast(msg, type);
  }, [showToast]);

  const totalItems = pagination.total || 0;
  const totalPages = pagination.last_page || 1;
  const currentPageNum = pagination.current_page || 1;

  const goToPage = (page) => { if (page >= 1 && page <= totalPages) { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); } };
  const goToPreviousPage = () => { if (currentPageNum > 1) { setCurrentPage(currentPageNum - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); } };
  const goToNextPage = () => { if (currentPageNum < totalPages) { setCurrentPage(currentPageNum + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); } };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage + 1 < maxPagesToShow) { startPage = Math.max(1, endPage - maxPagesToShow + 1); }
    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
    return pageNumbers;
  };

  const getStatusBadge = (isActive) => isActive ? <span className="badge badge-green">Active</span> : <span className="badge badge-amber">Inactive</span>;
  const isActive = (item) => item.is_active === true;

  const validateForm = (values) => {
    const errors = {};
    if (!values.title) errors.title = 'Title is required';
    if (!values.content) errors.content = 'Content is required';
    return errors;
  };

  const getFormValues = () => ({
    title: titleRef.current?.value?.trim() || '',
    excerpt: excerptRef.current?.value?.trim() || '',
    content: contentRef.current?.value?.trim() || '',
    category: categoryRef.current?.value?.trim() || '',
    author: authorRef.current?.value?.trim() || '',
    author_role: authorRoleRef.current?.value?.trim() || '',
    author_bio: authorBioRef.current?.value?.trim() || '',
    tag: tagRef.current?.value?.trim() || '',
    published_at: publishedRef.current?.value || null,
    read_time: parseInt(readTimeRef.current?.value) || 5,
    is_featured: isFeaturedRef.current?.checked || false,
    is_trending: isTrendingRef.current?.checked || false,
    order: parseInt(orderRef.current?.value) || 0,
    is_active: activeRef.current?.checked || false,
    featured_image: imageRef.current?.files?.[0] || null,
  });

  const getEditFormValues = () => ({
    title: editTitleRef.current?.value?.trim() || '',
    excerpt: editExcerptRef.current?.value?.trim() || '',
    content: editContentRef.current?.value?.trim() || '',
    category: editCategoryRef.current?.value?.trim() || '',
    author: editAuthorRef.current?.value?.trim() || '',
    author_role: editAuthorRoleRef.current?.value?.trim() || '',
    author_bio: editAuthorBioRef.current?.value?.trim() || '',
    tag: editTagRef.current?.value?.trim() || '',
    published_at: editPublishedRef.current?.value || null,
    read_time: parseInt(editReadTimeRef.current?.value) || 5,
    is_featured: editIsFeaturedRef.current?.checked || false,
    is_trending: editIsTrendingRef.current?.checked || false,
    order: parseInt(editOrderRef.current?.value) || 0,
    is_active: editActiveRef.current?.checked || false,
    featured_image: editImageRef.current?.files?.[0] || null,
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // ==================== MODALS ====================

  const openCreateModal = () => {
    editingIdRef.current = null;
    setTimeout(() => {
      if (titleRef.current) titleRef.current.value = '';
      if (excerptRef.current) excerptRef.current.value = '';
      if (contentRef.current) contentRef.current.value = '';
      if (categoryRef.current) categoryRef.current.value = '';
      if (authorRef.current) authorRef.current.value = '';
      if (authorRoleRef.current) authorRoleRef.current.value = '';
      if (authorBioRef.current) authorBioRef.current.value = '';
      if (tagRef.current) tagRef.current.value = '';
      if (publishedRef.current) publishedRef.current.value = new Date().toISOString().split('T')[0];
      if (readTimeRef.current) readTimeRef.current.value = '5';
      if (isFeaturedRef.current) isFeaturedRef.current.checked = false;
      if (isTrendingRef.current) isTrendingRef.current.checked = false;
      if (orderRef.current) orderRef.current.value = '0';
      if (activeRef.current) activeRef.current.checked = true;
      if (imageRef.current) imageRef.current.value = null;
    }, 0);
    openModal(
      <div>
        <div className="modal-header"><div className="modal-title"><Plus size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Create New Blog Post</div><button className="modal-close" onClick={closeModal}>×</button></div>
        <div className="modal-body">
          <div className="form-group"><label className="form-label">Title <span style={{ color: 'var(--red)' }}>*</span></label><input className="form-input" placeholder="Post title" ref={titleRef} /></div>
          <div className="form-group"><label className="form-label">Excerpt</label><textarea className="form-textarea" rows="2" ref={excerptRef} /></div>
          <div className="form-group"><label className="form-label">Content (HTML) <span style={{ color: 'var(--red)' }}>*</span></label><textarea className="form-textarea" rows="6" ref={contentRef} /></div>
          <div className="form-group"><label className="form-label">Featured Image</label><input type="file" className="form-file" ref={imageRef} accept="image/*" /></div>
          <div className="form-group"><label className="form-label">Category</label><input className="form-input" placeholder="Strategy" ref={categoryRef} /></div>
          <div className="form-group"><label className="form-label">Author</label><input className="form-input" placeholder="Pastor John" ref={authorRef} /></div>
          <div className="form-group"><label className="form-label">Author Role</label><input className="form-input" placeholder="Lead Pastor" ref={authorRoleRef} /></div>
          <div className="form-group"><label className="form-label">Author Bio</label><textarea className="form-textarea" rows="2" ref={authorBioRef} /></div>
          <div className="form-group"><label className="form-label">Tag</label><input className="form-input" placeholder="Communication" ref={tagRef} /></div>
          <div className="form-group"><label className="form-label">Published Date</label><input type="date" className="form-input" ref={publishedRef} /></div>
          <div className="form-group"><label className="form-label">Read Time (min)</label><input className="form-input" type="number" min="1" ref={readTimeRef} defaultValue="5" /></div>
          <div className="form-group" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}><input type="checkbox" ref={isFeaturedRef} /> Featured</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}><input type="checkbox" ref={isTrendingRef} /> Trending</label>
          </div>
          <div className="form-group"><label className="form-label">Order</label><input className="form-input" type="number" min="0" ref={orderRef} defaultValue="0" /></div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" ref={activeRef} defaultChecked={true} /><span>Active</span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button className="btn btn-gold btn-sm" disabled={isSubmitting} onClick={async () => {
            const values = getFormValues();
            const errors = validateForm(values);
            if (Object.keys(errors).length > 0) { showToastMessage(Object.values(errors)[0], 'error'); return; }
            setIsSubmitting(true);
            try {
              const formData = new FormData();
              Object.keys(values).forEach(key => {
                if (values[key] !== null && values[key] !== undefined) {
                  formData.append(key, values[key]);
                }
              });
              const result = await dispatch(createBlogPost(formData));
              if (result.payload?.success) { closeModal(); await dispatch(getBlogPosts({ per_page: itemsPerPage, page: currentPage })); showToastMessage('Blog post created successfully!', 'success'); }
              else if (result.error) showToastMessage(result.payload?.message || 'Failed to create post', 'error');
            } catch (err) { showToastMessage(err.message || 'Failed to create post', 'error'); }
            finally { setIsSubmitting(false); }
          }}>{isSubmitting ? 'Creating...' : 'Create Post'}</button>
        </div>
      </div>
    );
  };

  const handleEditPost = (item) => {
    editingIdRef.current = item.id;
    setTimeout(() => {
      if (editTitleRef.current) editTitleRef.current.value = item.title || '';
      if (editExcerptRef.current) editExcerptRef.current.value = item.excerpt || '';
      if (editContentRef.current) editContentRef.current.value = item.content || '';
      if (editCategoryRef.current) editCategoryRef.current.value = item.category || '';
      if (editAuthorRef.current) editAuthorRef.current.value = item.author || '';
      if (editAuthorRoleRef.current) editAuthorRoleRef.current.value = item.author_role || '';
      if (editAuthorBioRef.current) editAuthorBioRef.current.value = item.author_bio || '';
      if (editTagRef.current) editTagRef.current.value = item.tag || '';
      if (editPublishedRef.current) editPublishedRef.current.value = item.published_at ? item.published_at.split('T')[0] : '';
      if (editReadTimeRef.current) editReadTimeRef.current.value = item.read_time || 5;
      if (editIsFeaturedRef.current) editIsFeaturedRef.current.checked = !!item.is_featured;
      if (editIsTrendingRef.current) editIsTrendingRef.current.checked = !!item.is_trending;
      if (editOrderRef.current) editOrderRef.current.value = item.order || 0;
      if (editActiveRef.current) editActiveRef.current.checked = item.is_active !== false;
      if (editImageRef.current) editImageRef.current.value = null;
    }, 0);
    openModal(
      <div>
        <div className="modal-header"><div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Edit Post: {item.title}</div><button className="modal-close" onClick={closeModal}>×</button></div>
        <div className="modal-body">
          <div className="form-group"><label className="form-label">Title <span style={{ color: 'var(--red)' }}>*</span></label><input className="form-input" ref={editTitleRef} /></div>
          <div className="form-group"><label className="form-label">Excerpt</label><textarea className="form-textarea" rows="2" ref={editExcerptRef} /></div>
          <div className="form-group"><label className="form-label">Content (HTML) <span style={{ color: 'var(--red)' }}>*</span></label><textarea className="form-textarea" rows="6" ref={editContentRef} /></div>
          <div className="form-group"><label className="form-label">Change Image</label><input type="file" className="form-file" ref={editImageRef} accept="image/*" /></div>
          <div className="form-group"><label className="form-label">Category</label><input className="form-input" ref={editCategoryRef} /></div>
          <div className="form-group"><label className="form-label">Author</label><input className="form-input" ref={editAuthorRef} /></div>
          <div className="form-group"><label className="form-label">Author Role</label><input className="form-input" ref={editAuthorRoleRef} /></div>
          <div className="form-group"><label className="form-label">Author Bio</label><textarea className="form-textarea" rows="2" ref={editAuthorBioRef} /></div>
          <div className="form-group"><label className="form-label">Tag</label><input className="form-input" ref={editTagRef} /></div>
          <div className="form-group"><label className="form-label">Published Date</label><input type="date" className="form-input" ref={editPublishedRef} /></div>
          <div className="form-group"><label className="form-label">Read Time (min)</label><input className="form-input" type="number" min="1" ref={editReadTimeRef} /></div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}><input type="checkbox" ref={editIsFeaturedRef} /> Featured</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}><input type="checkbox" ref={editIsTrendingRef} /> Trending</label>
          </div>
          <div className="form-group"><label className="form-label">Order</label><input className="form-input" type="number" min="0" ref={editOrderRef} /></div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
              <input type="checkbox" ref={editActiveRef} /><span>Active</span>
            </label>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button className="btn btn-gold btn-sm" disabled={isSubmitting} onClick={async () => {
            const values = getEditFormValues();
            const errors = validateForm(values);
            if (Object.keys(errors).length > 0) { showToastMessage(Object.values(errors)[0], 'error'); return; }
            setIsSubmitting(true);
            try {
              const formData = new FormData();
              Object.keys(values).forEach(key => {
                if (values[key] !== null && values[key] !== undefined) {
                  formData.append(key, values[key]);
                }
              });
              const result = await dispatch(updateBlogPost({ id: item.id, data: formData }));
              if (result.payload?.success) { closeModal(); await dispatch(getBlogPosts({ per_page: itemsPerPage, page: currentPage })); showToastMessage('Post updated successfully!', 'success'); }
              else if (result.error) showToastMessage(result.payload?.message || 'Failed to update post', 'error');
            } catch (err) { showToastMessage(err.message || 'Failed to update post', 'error'); }
            finally { setIsSubmitting(false); }
          }}>{isSubmitting ? 'Saving...' : <><Save size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Save Changes</>}</button>
        </div>
      </div>
    );
  };

  const handleDeletePost = (item) => {
    openModal(
      <div>
        <div className="modal-header"><div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Delete Post</div><button className="modal-close" onClick={closeModal}>×</button></div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>Are you sure you want to delete post <strong>{item.title}</strong>?</p>
          <div style={{ padding: '12px', background: 'var(--red-bg)', borderRadius: '8px', fontSize: '13px', color: 'var(--red)' }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} /> This will permanently remove this post.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button className="btn btn-danger btn-sm" disabled={isSubmitting} onClick={async () => {
            setIsSubmitting(true);
            try {
              const result = await dispatch(deleteBlogPost(item.id));
              if (result.payload?.success) { closeModal(); await dispatch(getBlogPosts({ per_page: itemsPerPage, page: currentPage })); showToastMessage('Post deleted successfully.', 'error'); }
              else if (result.error) showToastMessage(result.payload?.message || 'Failed to delete post', 'error');
            } catch (err) { showToastMessage(err.message || 'Failed to delete post', 'error'); }
            finally { setIsSubmitting(false); }
          }}>{isSubmitting ? 'Deleting...' : 'Delete Post'}</button>
        </div>
      </div>
    );
  };

  const handleToggleStatus = async (id) => {
    setIsSubmitting(true);
    try {
      await dispatch(toggleBlogPostStatus(id)).unwrap();
      await dispatch(getBlogPosts({ per_page: itemsPerPage, page: currentPage }));
      showToastMessage('Status toggled', 'success');
    } catch (err) { showToastMessage(err.message || 'Toggle failed', 'error'); }
    finally { setIsSubmitting(false); }
  };

  const handleExport = async () => {
    setIsSubmitting(true);
    try {
      const result = await dispatch(exportBlogPosts({ search: searchTerm }));
      if (result.payload?.success) showToastMessage('Posts exported successfully', 'success');
      else showToastMessage(result.payload?.message || 'Export failed', 'error');
    } catch (err) { showToastMessage(err.message || 'Export failed', 'error'); }
    finally { setIsSubmitting(false); }
  };

  const handleViewDetails = (item) => {
    openModal(
      <div>
        <div className="modal-header"><div className="modal-title"><Eye size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Post Details</div><button className="modal-close" onClick={closeModal}>×</button></div>
        <div className="modal-body">
          <div style={{ display: 'grid', gap: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}><span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Title</span><span>{item.title}</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}><span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Author</span><span>{item.author || 'Unknown'}</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}><span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Category</span><span>{item.category || 'Uncategorized'}</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}><span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Featured</span><span>{item.is_featured ? 'Yes' : 'No'}</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}><span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Trending</span><span>{item.is_trending ? 'Yes' : 'No'}</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}><span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Status</span><span>{getStatusBadge(item.is_active)}</span></div>
            {item.created_at && <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px', fontSize: '13px' }}><span style={{ color: 'var(--text-light)', fontWeight: '500' }}>Created</span><span>{formatDate(item.created_at)}</span></div>}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button className="btn btn-gold btn-sm" onClick={() => { closeModal(); setTimeout(() => handleEditPost(item), 100); }}><Pencil size={16} /> Edit</button>
        </div>
      </div>
    );
  };

  // ==================== RENDER FUNCTIONS ====================

  const renderGridView = () => {
    return (
      <div className="posts-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
        {items.map(item => (
          <div key={item.id} className="card" style={{ borderTop: `3px solid ${item.is_active ? 'var(--gold)' : 'var(--text-light)'}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
              <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={22} color="var(--navy)" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--navy)' }}>{item.title}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>{item.author || 'Unknown'} · {item.category || 'General'}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                {getStatusBadge(item.is_active)}
                <label className="toggle" title={isActive(item) ? 'Click to deactivate' : 'Click to activate'}>
                  <input type="checkbox" checked={isActive(item)} onChange={(e) => handleToggleStatus(item.id)} disabled={isSubmitting || isSubmittingRedux} />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px', padding: '10px', background: 'var(--cream)', borderRadius: '8px', marginBottom: '12px' }}>
              <div style={{ flex: 1, textAlign: 'center' }}><div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Featured</div><div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--navy)', marginTop: '2px' }}>{item.is_featured ? 'Yes' : 'No'}</div></div>
              <div style={{ flex: 1, borderLeft: '1px solid var(--border)', paddingLeft: '12px' }}><div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Trending</div><div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--navy)', marginTop: '2px' }}>{item.is_trending ? 'Yes' : 'No'}</div></div>
              <div style={{ flex: 1, borderLeft: '1px solid var(--border)', paddingLeft: '12px' }}><div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Read Time</div><div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--navy)', marginTop: '2px' }}>{item.read_time || 0} min</div></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: '8px', borderTop: '1px solid var(--border-light)' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-mid)', flex: 1, marginRight: '8px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {item.excerpt || (item.content?.substring(0, 100) + '...')}
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <button className="btn btn-sm btn-icon btn-edit" onClick={() => handleViewDetails(item)} disabled={loading} title="View Details"><Eye size={14} /></button>
                <button className="btn btn-sm btn-icon btn-edit" onClick={() => handleEditPost(item)} disabled={loading} title="Edit"><Pencil size={14} /></button>
                <button className="btn btn-sm btn-icon btn-delete" onClick={() => handleDeletePost(item)} disabled={loading} title="Delete"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderListView = () => {
    return (
      <div className="card" style={{ marginBottom: '20px', padding: 0, overflow: 'hidden' }}>
        <div className="card-header" style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <div className="card-title"><ClipboardList size={16} style={{ verticalAlign: '-3px' }} /> Blog Posts</div>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Title</th><th>Author</th><th>Category</th><th>Status</th><th style={{ minWidth: '120px' }}>Actions</th></tr></thead>
            <tbody>
              {items.length > 0 ? items.map(item => (
                <tr key={item.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FileText size={14} color="var(--navy)" />
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', color: 'var(--navy)' }}>{item.title}</div>
                        {item.is_featured && <span className="badge badge-gold" style={{ background: 'var(--gold)', color: 'var(--navy)', fontSize: '9px' }}>Featured</span>}
                        {item.is_trending && <span className="badge badge-blue" style={{ background: 'var(--blue-bg)', color: 'var(--blue)', fontSize: '9px' }}>Trending</span>}
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: '13px', color: 'var(--text-mid)' }}>{item.author || '—'}</td>
                  <td style={{ fontSize: '13px', color: 'var(--text-mid)' }}>{item.category || '—'}</td>
                  <td>{getStatusBadge(item.is_active)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {isActive(item) ? <button className="btn btn-sm btn-icon btn-toggle" onClick={() => handleToggleStatus(item.id)} disabled={isSubmitting || isSubmittingRedux} title="Deactivate"><ToggleRight size={14} /></button> : <button className="btn btn-sm btn-icon btn-toggle" onClick={() => handleToggleStatus(item.id)} disabled={isSubmitting || isSubmittingRedux} title="Activate"><ToggleLeft size={14} /></button>}
                      <button className="btn btn-sm btn-icon btn-edit" onClick={() => handleViewDetails(item)} disabled={loading} title="View"><Eye size={14} /></button>
                      <button className="btn btn-sm btn-icon btn-edit" onClick={() => handleEditPost(item)} disabled={loading} title="Edit"><Pencil size={14} /></button>
                      <button className="btn btn-sm btn-icon btn-delete" onClick={() => handleDeletePost(item)} disabled={loading} title="Delete"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="5"><div className="empty-state"><div className="empty-icon"><FileText size={40} strokeWidth={1.5} /></div><div className="empty-title">No posts found</div><div className="empty-text">Create your first blog post to get started.</div></div></td></tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', padding: '14px 20px', borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>Showing <strong>{((currentPageNum - 1) * itemsPerPage) + 1}</strong> to <strong>{Math.min(currentPageNum * itemsPerPage, totalItems)}</strong> of <strong>{totalItems}</strong> posts</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
                <select className="form-select" style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }} value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} disabled={loading}>
                  <option value={3}>3</option><option value={6}>6</option><option value={9}>9</option><option value={12}>12</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button className="btn btn-ghost btn-sm" onClick={goToPreviousPage} disabled={currentPageNum === 1 || loading} style={{ opacity: currentPageNum === 1 ? 0.5 : 1 }}>← Prev</button>
                {getPageNumbers().map(page => <button key={page} className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`} onClick={() => goToPage(page)} disabled={loading}>{page}</button>)}
                <button className="btn btn-ghost btn-sm" onClick={goToNextPage} disabled={currentPageNum === totalPages || loading} style={{ opacity: currentPageNum === totalPages ? 0.5 : 1 }}>Next →</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ==================== STATS ====================
  const total = items.length;
  const activeCount = items.filter(s => s.is_active).length;
  const inactiveCount = items.filter(s => !s.is_active).length;
  const featuredCount = items.filter(s => s.is_featured).length;

  // ==================== MAIN RENDER ====================

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div><h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '700', color: 'var(--navy)' }}><FileText size={20} style={{ verticalAlign: '-4px' }} /> Blog Management</h2><p style={{ fontSize: '14px', color: 'var(--text-mid)' }}>Manage blog posts.</p></div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-gold btn-sm" onClick={openCreateModal} disabled={isSubmitting}>+ Add Post</button>
          <button className="btn btn-ghost btn-sm" onClick={handleExport} disabled={isSubmitting || loading}><Save size={14} style={{ verticalAlign: '-2px' }} /> Export</button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold"><div className="stat-label">Total Posts</div><div className="stat-value">{pagination.total || total}</div><div className="stat-change up">↑ total</div><div className="stat-icon"><FileText size={20} /></div></div>
        <div className="stat-card green"><div className="stat-label">Active</div><div className="stat-value">{activeCount}</div><div className="stat-change up">↑ active</div><div className="stat-icon"><CheckCircle2 size={20} /></div></div>
        <div className="stat-card amber"><div className="stat-label">Inactive</div><div className="stat-value">{inactiveCount}</div><div className="stat-change down">↓ inactive</div><div className="stat-icon"><XCircle size={20} /></div></div>
        <div className="stat-card blue"><div className="stat-label">Featured</div><div className="stat-value">{featuredCount}</div><div className="stat-change up">↑ featured</div><div className="stat-icon"><Star size={20} /></div></div>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}><span className="search-icon"><Search size={14} /></span><input type="text" placeholder="Search posts..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} /></div>
          <select className="form-select" style={{ width: '140px' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option><option value="active">Active</option><option value="inactive">Inactive</option>
          </select>
          <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
            <button className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-ghost'} btn-sm`} onClick={() => setViewMode('grid')}><LayoutGrid size={14} style={{ verticalAlign: '-2px' }} /> Grid</button>
            <button className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-ghost'} btn-sm`} onClick={() => setViewMode('list')}><List size={14} style={{ verticalAlign: '-2px' }} /> List</button>
          </div>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}><Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} /><div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading posts...</div></div>
      ) : (
        items.length > 0 ? (viewMode === 'grid' ? renderGridView() : renderListView()) :
        <div className="empty-state"><div className="empty-icon"><FileText size={40} strokeWidth={1.5} /></div><div className="empty-title">No posts found</div><div className="empty-text">Create your first blog post to get started.</div></div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
        .stat-card { position: relative; padding: 16px 20px; background: var(--white); border: 1px solid var(--border); border-radius: var(--radius); box-shadow: var(--shadow); transition: transform 0.2s, box-shadow 0.2s; cursor: default; }
        .stat-card:hover { transform: translateY(-2px); box-shadow: var(--shadow-lg); }
        .stat-card .stat-label { font-size: 12px; color: var(--text-light); text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
        .stat-card .stat-value { font-size: 28px; font-weight: 700; font-family: 'Playfair Display', serif; color: var(--navy); margin: 4px 0 2px; }
        .stat-card .stat-change { font-size: 12px; font-weight: 600; }
        .stat-card .stat-change.up { color: var(--green); }
        .stat-card .stat-change.down { color: var(--red); }
        .stat-card .stat-icon { position: absolute; top: 16px; right: 16px; opacity: 0.2; color: var(--navy); }
        .stat-card.gold .stat-icon { color: var(--gold); }
        .stat-card.green .stat-icon { color: var(--green); }
        .stat-card.amber .stat-icon { color: var(--amber); }
        .stat-card.blue .stat-icon { color: var(--blue); }
        .badge { padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 500; display: inline-block; }
        .badge-green { background: var(--green-bg); color: var(--green); }
        .badge-amber { background: var(--amber-bg); color: var(--amber); }
        .badge-blue { background: var(--blue-bg); color: var(--blue); }
        .badge-gold { background: var(--gold); color: var(--navy); }
        .toggle { position: relative; display: inline-block; width: 40px; height: 22px; flex-shrink: 0; }
        .toggle input { opacity: 0; width: 0; height: 0; }
        .toggle-slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background: #ccc; transition: 0.3s; border-radius: 22px; }
        .toggle-slider:before { position: absolute; content: ""; height: 16px; width: 16px; left: 3px; bottom: 3px; background: white; transition: 0.3s; border-radius: 50%; }
        .toggle input:checked + .toggle-slider { background: var(--gold); }
        .toggle input:checked + .toggle-slider:before { transform: translateX(18px); }
        .btn-edit { background: var(--blue-bg) !important; color: var(--blue) !important; border: 1px solid #93c5fd !important; }
        .btn-edit:hover { background: var(--blue) !important; color: white !important; border-color: var(--blue) !important; }
        .btn-delete { background: var(--red-bg) !important; color: var(--red) !important; border: 1px solid #fca5a5 !important; }
        .btn-delete:hover { background: var(--red) !important; color: white !important; border-color: var(--red) !important; }
        .btn-toggle { background: var(--cream) !important; color: var(--text-mid) !important; border: 1px solid var(--border) !important; }
        .btn-toggle:hover { background: var(--border) !important; color: var(--text) !important; }
        .btn-sm { padding: 4px 10px !important; font-size: 12px !important; }
        .btn-icon { padding: 4px 6px !important; }
        .btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .btn-gold { background: var(--gold) !important; color: var(--navy) !important; border: none !important; }
        .btn-gold:hover { background: var(--gold-dark) !important; color: var(--navy) !important; }
        .btn-danger { background: var(--red) !important; color: white !important; border: none !important; }
        .btn-danger:hover { background: var(--red-dark) !important; color: white !important; }
        .btn-ghost { background: transparent !important; color: var(--text) !important; border: 1px solid var(--border) !important; }
        .btn-ghost:hover { background: var(--cream) !important; color: var(--text) !important; }
        .btn-primary { background: var(--gold) !important; color: var(--navy) !important; border: none !important; }
        .btn-primary:hover { background: var(--gold-dark) !important; color: var(--navy) !important; }
        .empty-state { text-align: center; padding: 40px 0; }
        .empty-icon { font-size: 32px; margin-bottom: 12px; color: var(--text-light); }
        .empty-title { font-size: 16px; font-weight: 600; color: var(--text); margin-bottom: 4px; }
        .empty-text { font-size: 14px; color: var(--text-light); }
        .table-wrap { overflow-x: auto; }
        table { width: 100%; border-collapse: collapse; }
        th { text-align: left; padding: 10px 12px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; color: var(--text-light); border-bottom: 2px solid var(--border); background: var(--cream); }
        td { padding: 10px 12px; border-bottom: 1px solid var(--border-light); font-size: 13px; }
        tr:hover td { background: var(--cream); }
        .card { background: var(--white); border-radius: var(--radius); box-shadow: var(--shadow); padding: 16px; transition: transform 0.2s, box-shadow 0.2s; }
        .card:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }
        .search-box { display: flex; align-items: center; background: var(--white); border: 1px solid var(--border); border-radius: var(--radius-sm); padding: 0 10px; transition: border-color 0.2s; height: 36px; box-sizing: border-box; }
        .search-box:focus-within { border-color: var(--gold); }
        .search-box input { border: none; padding: 8px 0; font-size: 13px; outline: none; width: 100%; background: transparent; color: var(--text); height: 100%; font-family: inherit; margin: 0; line-height: normal; box-sizing: border-box; }
        .search-box .search-icon { display: flex; align-items: center; justify-content: center; color: var(--text-light); margin-right: 6px; flex-shrink: 0; }
        @media (max-width: 1024px) { .stats-grid { grid-template-columns: repeat(2, 1fr) !important; } .posts-grid { grid-template-columns: 1fr !important; } }
        @media (max-width: 768px) { .stats-grid { grid-template-columns: 1fr !important; } .posts-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </>
  );
};

export default BlogPosts;